// Client-side MCO stress grid — same payment engine, one Official baseline.
// Varies harvest price × final yield; cost basket as ONE multiplier (Rogers).
// No Databricks / tracker_daily — Watts Official params only.

import { computeMcoProjectedPayment } from "./mcoModel.js";

/** Harvest cost basket at `costMult`× — shared so the grid and cell hover agree. */
export function stressedHarvestCosts(baseline, costMult = 1) {
  const baseHarvestCosts =
    baseline?.harvestCosts != null && baseline.harvestCosts !== ""
      ? Number(baseline.harvestCosts)
      : Number(baseline?.projectedCosts);
  return Number.isFinite(baseHarvestCosts) && baseHarvestCosts !== 0
    ? baseHarvestCosts * Number(costMult ?? 1)
    : baseHarvestCosts;
}

/**
 * @param {object} baseline — fields for computeMcoProjectedPayment (Official + farmer)
 * @param {object} [opts]
 * @param {number[]} [opts.yieldSteps] — final yields to sweep
 * @param {number[]} [opts.priceSteps] — harvest prices to sweep
 * @param {number} [opts.costMult=1] — harvest cost basket multiplier
 */
export function buildMcoStressGrid(baseline, opts = {}) {
  if (!baseline) {
    return { ready: false, blockers: ["baseline missing"], rows: [], cols: [], cells: [] };
  }

  const eay = Number(baseline.expectedYield);
  const pp = Number(baseline.projectedPrice);
  const yieldSteps =
    opts.yieldSteps || (eay > 0 ? [0.7, 0.8, 0.9, 1, 1.1].map((f) => Math.round(eay * f * 10) / 10) : []);
  const priceSteps =
    opts.priceSteps || (pp > 0 ? [0.75, 0.85, 0.95, 1, 1.05].map((f) => Math.round(pp * f * 100) / 100) : []);
  const costMult = Number(opts.costMult ?? 1);

  if (!yieldSteps.length || !priceSteps.length) {
    return {
      ready: false,
      blockers: ["need expected yield + projected price for grid"],
      rows: [],
      cols: [],
      cells: [],
    };
  }

  const stressedCosts = stressedHarvestCosts(baseline, costMult);

  const cells = [];
  let anyReady = false;
  const blockers = new Set();

  for (const fy of yieldSteps) {
    for (const hp of priceSteps) {
      const {
        ready,
        blockers: b,
        result,
      } = computeMcoProjectedPayment({
        ...baseline,
        finalYield: fy,
        harvestPrice: hp,
        harvestCosts: stressedCosts,
      });
      if (ready) anyReady = true;
      else (b || []).forEach((x) => blockers.add(x));
      cells.push({
        finalYield: fy,
        harvestPrice: hp,
        ready,
        mcoPayment: ready ? result.mcoPayment : null,
        paymentFactor: ready ? result.paymentFactor : null,
        triggerMargin: ready ? result.triggerMargin : null,
        harvestMargin: ready ? result.harvestMargin : null,
        protectionPerAcre: ready ? result.protectionPerAcre : null,
      });
    }
  }

  return {
    ready: anyReady,
    blockers: anyReady ? [] : [...blockers],
    rows: yieldSteps,
    cols: priceSteps,
    cells,
    costMult,
    eayBasis: "official",
  };
}

/** Lookup one cell. */
export function stressCellAt(grid, finalYield, harvestPrice) {
  if (!grid?.cells) return null;
  return grid.cells.find((c) => c.finalYield === finalYield && c.harvestPrice === harvestPrice) || null;
}
